import { Maybe, just } from 'maybeasy';
import { Error } from '../ErrorHandling';
import { Resource, ResourceWithMetadata } from '../Resource/Types';
import { SearchParameter, SearchParameters } from '../ListFilter/Types';
import { PaginationMetadata } from '@execonline-inc/resource';
import { currentUserStore } from '../CurrentUserStore';
import { defaultOrgFilter } from '../components/Form/OrganizationFilterDynamicTypeaheadSelect/Filters/Organization';
import { StringOption } from '../components/Options/Types';

interface ReadyState {
  page: number;
  searchParameters: SearchParameters;
  groupsResource: GroupsResource;
  selectedOrganization: Maybe<StringOption>;
}

export interface Ready extends ReadyState {
  kind: 'ready';
}

export interface UpdatingQuery extends ReadyState {
  kind: 'updating-query';
}

export interface UpdatingSearchParameters extends ReadyState {
  kind: 'updating-search-parameters';
}

export interface Sorting extends ReadyState {
  kind: 'sorting';
}
interface Initializing {
  kind: 'initializing';
  searchParameters: SearchParameters;
  selectedOrganization: Maybe<StringOption>;
}

interface Loading {
  kind: 'loading';
  page: number;
  searchParameters: SearchParameters;
  selectedOrganization: Maybe<StringOption>;
}

export interface LoadingMore extends ReadyState {
  kind: 'loading-more';
  page: number;
}

export const initializing = (searchParameters: SearchParameters): Initializing => {
  return {
    kind: 'initializing',
    selectedOrganization: currentUserStore.organizationOption,
    searchParameters: [...searchParameters, ...defaultOrgFilter().searchParameters],
  };
};

export const loading = (
  state: Initializing | Sorting | Ready | UpdatingSearchParameters,
  page: number,
): Loading => {
  const { kind, ...previous } = state;
  return {
    kind: 'loading',
    ...previous,
    page,
  };
};

export const loadingMore = (state: Ready, page: number): LoadingMore => {
  const { kind, ...previous } = state;
  return {
    kind: 'loading-more',
    ...previous,
    page,
  };
};

export const ready = (
  state: Loading | LoadingMore | UpdatingQuery | UpdatingSearchParameters | Ready,
  groupsResource: GroupsResource,
): Ready => {
  const { kind, ...previous } = state;
  return { kind: 'ready', ...previous, groupsResource };
};

const updatedSearchParameters = (
  searchParameters: SearchParameters,
  searchParameter: SearchParameter,
) => searchParameters.filter((sp) => sp.key !== searchParameter.key).concat([searchParameter]);

export const updatingSearchParameters = (
  searchParameters: SearchParameters,
  state: Ready,
): UpdatingSearchParameters => {
  const { kind, ...previous } = state;
  return {
    kind: 'updating-search-parameters',
    ...previous,
    searchParameters,
  };
};

export const updatingOrganizationParameters = (
  state: Ready,
  option: StringOption,
): UpdatingSearchParameters => {
  const { kind, ...previous } = state;

  return {
    kind: 'updating-search-parameters',
    ...previous,
    searchParameters: updatedSearchParameters(state.searchParameters, {
      key: 'organization_id',
      value: option.value,
    }),
    selectedOrganization: just(option),
  };
};

export const updatingQuery = (searchParameters: SearchParameters, state: Ready): UpdatingQuery => {
  const { kind, ...previous } = state;
  return {
    kind: 'updating-query',
    ...previous,
    searchParameters,
  };
};

export const sorting = (searchParameters: SearchParameters, state: Ready): Sorting => {
  const { kind, ...previous } = state;

  return {
    kind: 'sorting',
    ...previous,
    searchParameters: searchParameters,
  };
};

export type State =
  | Initializing
  | Loading
  | LoadingMore
  | Ready
  | UpdatingQuery
  | UpdatingSearchParameters
  | Sorting
  | Error;

export interface Group {
  id: number;
  name: string;
  organization: string;
  totalEnrollmentsCount: number;
  lastLoginAt: Maybe<Date>;
  status: GroupStatus;
  participants: Maybe<Participant[]>;
}

export type GroupStatus = 'Active' | 'Inactive';

export type GroupResource = Resource<Group>;

export type GroupsMetadata = PaginationMetadata;

export type GroupResources = GroupResource[];

export type GroupsResource = ResourceWithMetadata<GroupResources, GroupsMetadata>;

export interface Participant {
  id: number;
  name: string;
}

export type SortableColumn =
  | 'group_name'
  | 'participants'
  | 'organization'
  | 'last_sign_in_at'
  | 'total_enrollments_count'
  | 'status';
